/**
 * App Experience (spec 2026-10-23 §5): what new members see when they first
 * open the app. The owner uses this page himself, so it speaks plainly, says
 * what is wrong and how to fix it, and never leaves a dead end.
 *
 * The route is wrapped in `experience.read` (App.tsx). Changing anything needs
 * `experience.write`; without it the page is read-only and says so, as the
 * Settings page does. The data is the UNCACHED admin view, shared by the tabs.
 */
import * as Tabs from '@radix-ui/react-tabs';
import { AlertCircle, Clapperboard, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { IntroVideoTab } from './IntroVideoTab';
import { CONFLICT_TEXT, useAppExperience } from './useAppExperience';
import { WelcomeTab } from './WelcomeTab';

const TAB_CLASS =
  'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=active]:bg-primary data-[state=active]:text-primary-foreground';

function Banner({ tone, children, onReload, busy }: { tone: 'warn' | 'error'; children: string; onReload: () => void; busy: boolean }) {
  const colors =
    tone === 'error'
      ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
      : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100';
  return (
    <div role="alert" className={`flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm ${colors}`}>
      <span className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {children}
      </span>
      <button
        type="button"
        onClick={onReload}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md border border-current px-3 py-1 text-xs font-medium disabled:opacity-50"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
        Reload
      </button>
    </div>
  );
}

export function AppExperiencePage() {
  const data = useAppExperience();
  const { hasPermission, loaded } = useAdminPermissions();
  const canWrite = hasPermission('experience.write');
  const { view, loading, loadError, stale, reload } = data;
  const doReload = () => void reload();

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Clapperboard className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">App Experience</h2>
          <p className="text-muted-foreground">Control what new members see when they open the app for the first time.</p>
        </div>
      </div>

      {loaded && !canWrite && (
        <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
          You can only view this page. To make changes, ask a Super Admin for the <code>experience.write</code> permission.
        </div>
      )}

      {stale && (
        <Banner tone="warn" onReload={doReload} busy={loading}>
          {CONFLICT_TEXT}
        </Banner>
      )}
      {loadError && view && (
        <Banner tone="error" onReload={doReload} busy={loading}>
          {`The page could not reload. ${loadError}`}
        </Banner>
      )}

      {!view && loading && (
        <div className="space-y-4" aria-busy="true" aria-label="Loading">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )}

      {!view && !loading && loadError && (
        <div role="alert" className="rounded-lg border bg-card p-8 text-center">
          <p className="mb-1 font-medium">The page could not load.</p>
          <p className="mb-4 text-sm text-muted-foreground">{loadError}</p>
          <button
            type="button"
            onClick={doReload}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      )}

      {view && (
        <Tabs.Root defaultValue="intro" className="space-y-4">
          <Tabs.List aria-label="Parts of the app experience" className="inline-flex gap-1 rounded-lg border bg-card p-1">
            <Tabs.Trigger value="intro" className={TAB_CLASS}>
              Intro video
            </Tabs.Trigger>
            <Tabs.Trigger value="welcome" className={TAB_CLASS}>
              Abby welcome
            </Tabs.Trigger>
          </Tabs.List>
          {/* forceMount + hidden: switching tabs must not unmount a tab, or an
              upload in progress and a draft that is not published are lost. */}
          <Tabs.Content value="intro" forceMount className="focus-visible:outline-none data-[state=inactive]:hidden">
            <IntroVideoTab data={data} rows={view.history.intro_video} canWrite={loaded && canWrite} />
          </Tabs.Content>
          <Tabs.Content value="welcome" forceMount className="focus-visible:outline-none data-[state=inactive]:hidden">
            <WelcomeTab data={data} rows={view.history.abby_welcome} mediaOrigin={view.media_origin} canWrite={loaded && canWrite} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </div>
  );
}
